import { type RefObject, useCallback, useState } from 'react'

import { useHandleDrag } from '@ai-color-modules/hooks/useHandleDrag'
import { useImageUpload } from '@ai-color-modules/hooks/useImageUpload'
import { isOutcomeUnknownError, parseStructuredError } from '@modules/error/errorCodes'

interface UseSimulationImageUploadOptions {
  fileInputRef: RefObject<HTMLInputElement | null>
  s3Upload: (file: File) => Promise<void>
  uploading: boolean
  /** 画像以外 (PDF 等) を扱うホスト向けに HEIC変換・圧縮をスキップする。既定 false (AI カラーの挙動を変えない) */
  skipCompression?: boolean
}

export const useSimulationImageUpload = ({
  fileInputRef,
  s3Upload,
  uploading,
  skipCompression = false,
}: UseSimulationImageUploadOptions) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  // s3Upload（アップロード実行）時のエラー。変換時エラーは base hook の error を使う。
  const [uploadError, setUploadError] = useState<string | null>(null)
  // 直前のアップロード失敗が、サーバーの処理結果が分からない種類（通信の途中で切れた等）か。
  // 「失敗したので〇〇は起きていない」というホストの一文（`uploadErrorNote`）を、この場合は出さない
  const [uploadOutcomeUnknown, setUploadOutcomeUnknown] = useState(false)

  const {
    handleImageUpload: baseHandleImageUpload,
    imagePreviews,
    uploadFile,
    setUploadFile,
    isProcessing,
    error: conversionError,
    setError: setConversionError,
  } = useImageUpload({
    resetSelections: () => {},
    skipCompression,
  })

  // 変換中・変換失敗エラーとアップロードエラーを集約してダイアログに表示する
  const error = uploadError ?? conversionError

  const handleImageUpload = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0] ?? null
      if (file) {
        setUploadError(null)
        setIsDialogOpen(true)
      }
      // uploadFile / imagePreviews / isProcessing / error は base hook が一元管理する
      // （生ファイルを直接 setUploadFile せず、変換後ファイルのみを送信対象にする）
      void baseHandleImageUpload(event)
    },
    [baseHandleImageUpload],
  )

  const dragHandlers = useHandleDrag({
    handleImageUpload,
  })

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setIsDialogOpen(open)
      if (!open) {
        setUploadError(null)
        setConversionError(null)
        setUploadFile(null)
      }
    },
    [setUploadFile, setConversionError],
  )

  const handleSelectClick = useCallback(() => {
    fileInputRef.current?.click()
  }, [fileInputRef])

  const handleDropZoneKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        handleSelectClick()
      }
    },
    [handleSelectClick],
  )

  const handleProceed = useCallback(async () => {
    // 変換完了前・変換失敗時・アップロード中は実行しない（生HEIC送信を防止）
    if (!uploadFile || uploading || isProcessing || conversionError) {
      return
    }
    setUploadError(null)
    setUploadOutcomeUnknown(false)
    try {
      await s3Upload(uploadFile)
      setIsDialogOpen(false)
      setUploadFile(null)
    } catch (err) {
      // err.messageをそのまま出すと、Connectのエラーはサーバー内部の文言になる
      // （STG 2026-09-17のAIカラー画像アップロード:
      // 「[failed_precondition] required=10 available=-45 floor=-50: credit: insufficient credit balance」）。
      // エラーコードが載っていれば日本語文言に、無ければ汎用文言に落ちる。
      setUploadError(parseStructuredError(err).message)
      setUploadOutcomeUnknown(isOutcomeUnknownError(err))
    }
  }, [uploadFile, s3Upload, uploading, isProcessing, conversionError, setUploadFile])

  return {
    handleImageUpload,
    imagePreviews,
    uploadFile,
    isDialogOpen,
    error,
    uploadOutcomeUnknown: uploadError != null && uploadOutcomeUnknown,
    isProcessing,
    handleOpenChange,
    handleSelectClick,
    handleDropZoneKeyDown,
    handleProceed,
    uploading,
    ...dragHandlers,
  }
}
